import GoBackButton from '@app/components/atoms/GoBackButton'
import SlopeTableHeaderPreview from '@app/components/molecules/SlopeTableHeaderPreview/SlopeTableHeaderPreview'
import Spacer from '@app/components/atoms/Spacer'
import {
  Field,
  FieldButton,
  FieldButtonIcon,
  RequiredIndicator,
  TextField,
} from '@app/components/molecules/FormFields'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import { MITee } from '@app/evaluation-core/types'
import getEnv from '@app/environments/getEnv'
import { navigate } from '@app/navigation'
import { useSnackbar } from '@app/snackbar'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import theme from '@app/ui/theme'
import { useRoute } from '@react-navigation/native'
import { FaAngleDown } from 'react-icons/fa'
import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import * as S from './Slope.style'
import Tees from './Tees'
import {
  createManualSlopeTable,
  updateManualSlopeTable,
  getManualSlopeTable,
  SavedSlopeTable,
  Tee,
} from '../manualSlopeTableService'
import DownloadManualSlopeTableModal from './DownloadManualSlopeTableModal'
import {
  DEFAULT_HOLE_RANGE,
  FIRST_HOLE,
  formatHoleRange,
  HoleRange,
  holeRangeLabel,
  LAST_HOLE,
} from '../holeRange'
import HoleRangeModal from './HoleRangeModal'
import DateField from './DateField'
import AddTeesModal from './AddTeesModal'

type Gender = 'MEN' | 'WOMEN'

const BASE_URL_TMP = getEnv().api + '/ratings'

interface SlopeTableParams {
  tees: Tee[]
  par: number
  courseName: string
  clubName: string
  isDraft: boolean
  gender: 'M' | 'W'
  ratingDate: string
  ratingName: string
  startHole: number
  endHole: number
}

function openWindowAsPost(
  url: string,
  windowoption: string | null,
  name: string,
  params: SlopeTableParams
): void {
  const form = document.createElement('form')
  form.setAttribute('method', 'post')
  form.setAttribute('action', url)
  form.setAttribute('target', name)
  for (const i in params) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = i
    if (typeof params[i] === 'object') {
      input.value = JSON.stringify(params[i])
    } else {
      input.value = params[i]
    }
    form.appendChild(input)
  }
  document.body.appendChild(form)
  //note I am using a post.htm page since I did not want to make double request to the page
  //it might have some Page_Load call which might screw things up.
  window.open('post.htm', name, '_blank')
  form.submit()
  document.body.removeChild(form)
}

type FormErrors = {
  clubName?: string
  ratingDate?: string
  holeRange?: string
  tees?: string
}

const Slope = () => {
  const route = useRoute<any>()
  const slopeTableId = route.params?.slopeTableId as string | undefined
  const clubId = route.params?.clubId as string | undefined
  const [tees, setTees] = useState<Tee[]>([])
  const [gender, setGender] = useState<Gender>('MEN')
  const [courseName, setCourseNameValue] = useState<string>()
  const [clubName, setClubNameValue] = useState<string>(
    route.params?.clubName?.toUpperCase()
  )
  // The club and course name are printed in capitals on the slope table, so
  // the form holds them that way instead of leaving it to the header styling:
  // this value is what reaches the generated table, and the preview above the
  // form is only honest if it shows what will be printed. Going through the
  // setters covers a name typed in, a name pasted in and a name read back off
  // a saved table.
  const setCourseName = (value?: string) =>
    setCourseNameValue(value?.toUpperCase())
  const setClubName = (value: string) => setClubNameValue(value.toUpperCase())
  const [isDraft, setIsDraft] = useState<boolean>(false)
  const [par, setPar] = useState<number>(3)
  const [ratingDate, setRatingDate] = useState<string>('')
  const [ratingName, setRatingName] = useState<string>('')
  const [startHole, setStartHole] = useState<number>(
    DEFAULT_HOLE_RANGE.startHole
  )
  const [endHole, setEndHole] = useState<number>(DEFAULT_HOLE_RANGE.endHole)
  const [saving, setSaving] = useState(false)
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [showHoleRangeModal, setShowHoleRangeModal] = useState(false)
  const [showAddTeesModal, setShowAddTeesModal] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const isEditMode = !!slopeTableId
  const isGlobalMode = !clubId
  // What the club is called in the club list, which is what the reset button
  // beside the club name puts back.
  const originalClubName = route.params?.clubName?.toUpperCase() as
    | string
    | undefined

  // Reset form when navigating to a different club (route params change)
  useEffect(() => {
    if (!slopeTableId) {
      setClubName(route.params?.clubName || '')
      setTees([])
      setGender('MEN')
      setCourseName(undefined)
      setIsDraft(false)
      setPar(3)
      setRatingDate('')
      setRatingName('')
      setStartHole(DEFAULT_HOLE_RANGE.startHole)
      setEndHole(DEFAULT_HOLE_RANGE.endHole)
      setErrors({})
    }
  }, [clubId])

  useEffect(() => {
    if (!slopeTableId || !clubId) return
    getManualSlopeTable(clubId, slopeTableId).then((table) => {
      if (!table) return
      setTees(table.tees)
      setGender(table.gender === 'M' ? 'MEN' : 'WOMEN')
      setCourseName(table.courseName)
      setClubName(table.clubName)
      setIsDraft(table.isDraft)
      setPar(table.par)
      setRatingDate(table.ratingDate)
      setRatingName(table.ratingName)
      // A table saved before the range could be picked has none, so the form
      // opens on a full round and the choice is there to be changed
      const range = {
        startHole: table.startHole ?? DEFAULT_HOLE_RANGE.startHole,
        endHole: table.endHole ?? DEFAULT_HOLE_RANGE.endHole,
      }
      setStartHole(range.startHole)
      setEndHole(range.endHole)
    })
  }, [slopeTableId, clubId])

  const [openSnackbar] = useSnackbar()
  const selectGender = (gender: Gender) => () => setGender(gender)

  const holeRange = formatHoleRange(startHole, endHole)

  const selectHoleRange = (range: HoleRange) => {
    setStartHole(range.startHole)
    setEndHole(range.endHole)
    setErrors((e) => ({ ...e, holeRange: undefined }))
  }

  const validate = (): boolean => {
    const next: FormErrors = {}
    if (!clubName?.trim()) next.clubName = 'Please enter a club name'
    if (!ratingDate) next.ratingDate = 'Please select a rating date'
    if (
      !startHole ||
      !endHole ||
      startHole < FIRST_HOLE ||
      endHole > LAST_HOLE ||
      startHole > endHole
    )
      next.holeRange = `Enter a hole range between ${FIRST_HOLE} and ${LAST_HOLE}, starting at or before the last hole`
    if (!tees.length)
      next.tees = 'Add at least one tee to generate a slope table'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const addTee = (tee: Tee) => {
    if (tees.find((t) => t.title === tee.title)) {
      openSnackbar('Tee with this name already exists')
      return
    }
    setTees([...tees, tee])
    if (errors.tees) setErrors((e) => ({ ...e, tees: undefined }))
  }

  // A whole set of tees at once. A name already on the form is left alone
  // rather than added twice, and the form says how many were passed over.
  const addTees = (newTees: Tee[]) => {
    const accepted = newTees.filter(
      (newTee) =>
        !tees.some(
          (tee) => tee.title.toLowerCase() === newTee.title.toLowerCase()
        )
    )
    const skipped = newTees.length - accepted.length
    if (!accepted.length) {
      openSnackbar('Those tees are already added')
      return
    }
    setTees([...tees, ...accepted])
    setErrors((e) => ({ ...e, tees: undefined }))
    if (skipped)
      openSnackbar(
        skipped === 1
          ? '1 tee was already added and was skipped'
          : `${skipped} tees were already added and were skipped`
      )
  }

  const deleteTee = (teeToDelete: Tee) => {
    const filteredTees = tees.filter((tee) => tee.title !== teeToDelete.title)
    setTees(filteredTees)
    dispatch({
      type: 'SET_MODAL',
      params: null,
    })
  }

  const editTee = (teeToUpdate: Tee, update: Tee) => {
    const updatedTees = tees.map((tee) =>
      tee.title === teeToUpdate.title ? update : tee
    )
    setTees(updatedTees)
  }

  const handleTeeColorSave = (teeColors: string[], teeNames: string[]) => {
    const updatedTees = tees.map((tee, index) => ({
      ...tee,
      color: teeColors[index] || tee.color,
      title: teeNames[index] || tee.title,
    }))
    setTees(updatedTees)
    dispatch({
      type: 'SET_MODAL',
      params: null,
    })
  }

  const openTeeColorPicker = () => {
    // Convert our Tee type to MITee type for the color picker
    const miTees: MITee[] = tees.map((tee, index) => ({
      teeIndex: index,
      teeNumber: index + 1,
      name: tee.title,
      shots: {} as any,
      golfers: [],
      courseId: '',
      courseName: courseName || '',
      id: `${index}`,
      color: tee.color || '#ffffff',
    }))

    dispatch({
      type: 'SET_MODAL',
      params: {
        mode: 'teeColorPicker',
        props: {
          tees: miTees,
          onSave: handleTeeColorSave,
        },
      },
    })
  }

  const openTeeOverlay = (tee?: Tee) => () =>
    dispatch({
      type: 'SET_MODAL',
      params: {
        mode: 'addTee',
        props: tee
          ? { type: 'edit', addTee, deleteTee, editTee, tee }
          : { addTee, deleteTee, editTee },
      },
    })

  const dispatch = useDispatch<any>()
  return (
    <S.Wrapper>
      <Spacer height={24} />
      {!isGlobalMode && (
        <GoBackButton
          text="Go back to club"
          onPress={() => navigate('ClubManagement')}
          portraitMode={screen.width < screen.height}
          style={{ marginLeft: 24 }}
        />
      )}

      <S.ContentCard>
        <S.CardHeader>
          <S.CardHeaderText>
            <S.ContentHeading>
              {isEditMode ? 'Edit Slope Table' : 'Create Slope Tables'}
            </S.ContentHeading>
            <S.Subtitle>
              Fill in the course information and add at least one tee, then save
              or download the slope table.
            </S.Subtitle>
            <div style={{ display: 'none' }}>
              <RoundedButton
                width={124}
                title="Clear all"
                onPress={() => setTees([])}
              />
              <Spacer width={20} />
              <RoundedButton
                width={189}
                title="Generate slope table "
                onPress={() => {
                  if (!validate()) return
                  openWindowAsPost(
                    `${BASE_URL_TMP}/forms/slopetable/`,
                    null,
                    'Slope Table',
                    {
                      tees,
                      par,
                      courseName: courseName || '',
                      clubName,
                      isDraft,
                      gender: gender === 'MEN' ? 'M' : 'W',
                      ratingDate,
                      ratingName,
                      startHole,
                      endHole,
                    }
                  )
                }}
                style={{
                  backgroundColor: theme.colors.secondary2,
                }}
              />
            </div>
          </S.CardHeaderText>
          <SlopeTableHeaderPreview
            clubName={clubName}
            courseName={courseName}
            ratingName={ratingName}
            holeRange={holeRange}
            totalPar={par}
            ratingDate={ratingDate}
            isDraft={isDraft}
            gender={gender === 'MEN' ? 'Herrar' : 'Damer'}
          />
        </S.CardHeader>
        <S.ContentSeparator />

        <TextField
          label="Club name"
          additionalText="Required. Printed at the top of the slope table."
          defaultValue={clubName || ''}
          uppercase
          canReset={!!originalClubName}
          resetValue={originalClubName}
          indicator={<RequiredIndicator filled={!!clubName?.trim()} />}
          hasError={!!errors.clubName}
          errorText={errors.clubName}
          onChange={(value) => {
            setClubName(value)
            if (errors.clubName)
              setErrors((e) => ({ ...e, clubName: undefined }))
          }}
        />
        <TextField
          label="Course name"
          additionalText="Optional."
          defaultValue={courseName || ''}
          uppercase
          onChange={setCourseName}
        />
        <TextField
          label="Rating Name"
          additionalText="Optional. This text will appear in the Slope Tables."
          defaultValue={ratingName}
          onChange={setRatingName}
        />
        <Field
          label="Rating Date"
          additionalText="Required. The date the course was rated."
          indicator={<RequiredIndicator filled={!!ratingDate} />}
          inputWidth={S.FIELD_WIDTH}
          hasError={!!errors.ratingDate}
          errorText={errors.ratingDate}
        >
          <div style={{ width: S.FIELD_WIDTH }}>
            <DateField
              value={ratingDate}
              hasError={!!errors.ratingDate}
              onChange={(value) => {
                setRatingDate(value)
                if (errors.ratingDate)
                  setErrors((e) => ({ ...e, ratingDate: undefined }))
              }}
            />
          </div>
        </Field>
        <TextField
          label="Par"
          additionalText="The par printed in the slope table header."
          defaultValue={String(par)}
          type="number"
          min="3"
          max="6"
          onChange={(value) => setPar(Number(value))}
        />
        <Field
          label="Holes"
          additionalText="The holes the table covers."
          inputWidth={S.FIELD_WIDTH}
          hasError={!!errors.holeRange}
          errorText={errors.holeRange}
        >
          <div style={{ width: S.FIELD_WIDTH }}>
            <FieldButton
              type="button"
              hasError={!!errors.holeRange}
              onClick={() => setShowHoleRangeModal(true)}
            >
              {holeRangeLabel({ startHole, endHole })}
              <FieldButtonIcon>
                <FaAngleDown size={14} color={theme.colors.secondary} />
              </FieldButtonIcon>
            </FieldButton>
          </div>
        </Field>
        <Field label="Gender" additionalText="Who the table is rated for.">
          <S.ButtonPair>
            <RoundedButton
              title="MEN"
              onPress={selectGender('MEN')}
              style={{
                backgroundColor:
                  gender === 'MEN' ? theme.colors.secondary2 : '',
              }}
            />
            <RoundedButton
              title="WOMEN"
              onPress={selectGender('WOMEN')}
              style={{
                backgroundColor:
                  gender === 'WOMEN' ? theme.colors.secondary2 : '',
              }}
            />
          </S.ButtonPair>
        </Field>
        <Field
          label="Is Draft"
          additionalText="A draft is marked (DRAFT) and dated the day it is generated."
        >
          <S.ButtonPair>
            <RoundedButton
              title="YES"
              onPress={() => setIsDraft(true)}
              style={{
                backgroundColor: isDraft ? theme.colors.secondary2 : '',
              }}
            />
            <RoundedButton
              title="NO"
              onPress={() => setIsDraft(false)}
              style={{
                backgroundColor: !isDraft ? theme.colors.secondary2 : '',
              }}
            />
          </S.ButtonPair>
        </Field>
        <Field
          label="Tees"
          additionalText="Required. At least one tee to generate a slope table."
          indicator={<RequiredIndicator filled={tees.length > 0} />}
          errorText={errors.tees}
        >
          <S.TeeContainer>
            {tees.map((tee, index) => (
              <Tees
                key={index}
                title={tee.title}
                courseRating={tee.courseRating}
                slopeRating={tee.slopeRating}
                color={tee.color}
                onClick={openTeeOverlay(tee)}
              />
            ))}
            <S.AddTeeCard hasError={!!errors.tees} onClick={openTeeOverlay()}>
              <S.TeeAddButton>
                <span>+</span>
              </S.TeeAddButton>
              <S.TeeButtonTitle>Add a tee</S.TeeButtonTitle>
            </S.AddTeeCard>
            <S.AddTeeCard
              hasError={!!errors.tees}
              onClick={() => setShowAddTeesModal(true)}
            >
              <S.TeeAddButton>
                <FeatherIcon name="layers" size={15} color="white" />
              </S.TeeAddButton>
              <S.TeeButtonTitle>Add a tee set</S.TeeButtonTitle>
            </S.AddTeeCard>
          </S.TeeContainer>
        </Field>

        <S.BottomButtonContainer>
          <RoundedButton
            width={124}
            title="Cancel"
            onPress={() =>
              isGlobalMode
                ? navigate('SearchOverview')
                : navigate('ClubManagement')
            }
          />
          <Spacer width={20} />
          {isGlobalMode ? (
            <RoundedButton
              width={124}
              title="Download"
              onPress={() => {
                if (!validate()) return
                setShowDownloadModal(true)
              }}
              style={{
                backgroundColor: theme.colors.secondary2,
              }}
            />
          ) : (
            <RoundedButton
              width={124}
              title={saving ? 'Saving...' : 'Save'}
              disabled={saving}
              onPress={async () => {
                if (!validate()) return
                if (!clubId) {
                  openSnackbar('Missing club ID')
                  return
                }
                setSaving(true)
                try {
                  const data = {
                    tees,
                    par,
                    courseName: courseName || '',
                    clubName,
                    clubId,
                    isDraft,
                    gender: (gender === 'MEN' ? 'M' : 'W') as 'M' | 'W',
                    ratingDate,
                    ratingName,
                    startHole,
                    endHole,
                  }
                  if (isEditMode) {
                    await updateManualSlopeTable(clubId, slopeTableId, data)
                    openSnackbar('Slope table updated')
                  } else {
                    await createManualSlopeTable(clubId, data)
                    openSnackbar('Slope table saved')
                  }
                  navigate('ClubManagement')
                } catch (err) {
                  openSnackbar('Failed to save slope table')
                  console.error(err)
                } finally {
                  setSaving(false)
                }
              }}
              style={{
                backgroundColor: theme.colors.secondary2,
              }}
            />
          )}
        </S.BottomButtonContainer>
      </S.ContentCard>

      {isGlobalMode && (
        <DownloadManualSlopeTableModal
          isVisible={showDownloadModal}
          table={
            showDownloadModal
              ? ({
                  tees,
                  par,
                  courseName: courseName || '',
                  clubName: clubName || '',
                  clubId: '',
                  isDraft,
                  gender: gender === 'MEN' ? 'M' : 'W',
                  ratingDate,
                  ratingName,
                  startHole,
                  endHole,
                } as SavedSlopeTable)
              : null
          }
          onClose={() => setShowDownloadModal(false)}
        />
      )}
      <AddTeesModal
        isOpen={showAddTeesModal}
        onClose={() => setShowAddTeesModal(false)}
        onAdd={addTees}
      />
      <HoleRangeModal
        isOpen={showHoleRangeModal}
        startHole={startHole}
        endHole={endHole}
        onClose={() => setShowHoleRangeModal(false)}
        onSelect={selectHoleRange}
      />
    </S.Wrapper>
  )
}
export default Slope
